import React from 'react'
import { IColumn, IExtendedColumn } from '../Types'
import KnockTypeFilterMap from '../Filter'
import KnockTypeCellMap from '../Cell'
import { TextCell } from '../Cell/TextCell'
import EnumFilter from '../Filter/EnumFilter'
import { isEnum, isSliceOfEnum } from './KnockHelpers'
import { findKeyInObject } from '@cbgms/base/utils/helpers'
import { KnockTypes, defaultKnockOperators } from '@cbgms/components/grid/Knock/KnockTypes'
import MaskedComponent, { UseVinUnmaskReturn } from './MaskedComponent'
import { useFeature } from '@cbgms/base/feature-flags'

export interface IWithKnockDataProps {
  data: any
  headers: IExtendedColumn[]

  /**
   * customCellRendererMap allows you to provide header keys for which to use a specific cell renderer.
   */
  customCellRendererMap?: { [key: string]: React.ComponentType<{ content: any }> }

  /**
   * A map of enum values. Where enum name is the key, and the values are represented in a string array.
   */
  enums?: Record<string, string[]>

  /**
   * Hook for VIN unmasking functionality. Required when VIN masking is enabled.
   */
  useVinUnmask?: (params: { vin: string; vehicleUUID?: string; workorderUUID?: string; resourceID?: string }) => UseVinUnmaskReturn

  /**
   * Override the default mapping from headers to columns for the grid
   *
   * @param columns An array of the columns after mapping them to the format of the grid
   * @param preMapColumns An array of columns from before they were mapped to the format of the grid
   * @param data The data as retrieved from the API
   */
  onColumnMap?: (columns: IColumn[], preMapColumns: IColumn[], data: any) => any

  /**
   * Override the default mapping from data to rows for the grid
   *
   * @param rows An array of rows that was created as a result of the default mapping of the data
   * @param preMapData The data as retrieved from the API
   * @param columns A list of columns
   */
  onRowMap?: (rows: any, preMapData: any, columns: IColumn[]) => any
}

function withListData(WrappedComponent: React.ComponentType<any>) {
  function ListDataMapper(props: IWithKnockDataProps) {
    const { data, headers, onColumnMap, onRowMap, customCellRendererMap, useVinUnmask } = props
    const hasVinMaskingFeature = useFeature('uk-vin-masking')

    if (!data) {
      return <WrappedComponent {...props} />
    }

    let knockHeaders = headers.map(header => {
      let filterComponent
      let defaultOperator

      if (header.key && !header.static) {
        const knockType = findKeyInObject(data.Structure, header.key)

        if (knockType) {
          filterComponent =
            isEnum(knockType) && props.enums
              ? EnumFilter(knockType, props.enums[knockType])
              : KnockTypeFilterMap[knockType as keyof typeof KnockTypeFilterMap]

          const normalizedType = isSliceOfEnum(knockType) ? KnockTypes.EnumArray : (knockType as keyof typeof defaultKnockOperators)
          defaultOperator = defaultKnockOperators[normalizedType]
        }
      }

      return {
        filterComponent,
        ...header,
        name: header.name || header.key.split('.')[1], // Temp
        filterOperator: header.filterOperator || defaultOperator || '='
      }
    })

    if (onColumnMap) {
      knockHeaders = onColumnMap(knockHeaders as IColumn[], headers, data)
    }

    function isKnockCellType(input: string): input is keyof typeof KnockTypeCellMap {
      return Object.keys(KnockTypeCellMap).includes(input)
    }

    interface PartialHeaderColumn {
      action?: any
      key: string
    }

    function getCellComponent(header: PartialHeaderColumn): React.ComponentType<{ content: any }> | undefined {
      if (header.action) return

      const customCellRenderer = customCellRendererMap?.[header.key]

      if (customCellRenderer) {
        return customCellRenderer
      }

      const cellType = findKeyInObject(data.Structure, header.key)

      const exactCellRendererMatch = isKnockCellType(cellType) ? KnockTypeCellMap[cellType] : undefined

      if (exactCellRendererMatch) {
        return exactCellRendererMatch
      }

      const isSliceCellRenderer = cellType.startsWith(KnockTypes.EnumArray)

      if (isSliceCellRenderer) {
        return KnockTypeCellMap[KnockTypes.EnumArray]
      }

      return TextCell
    }

    let knockData = data.Data.map((item: any, index: number) => ({
      id: index,
      hovered: false,
      data: item,
      cols: knockHeaders.map(header => {
        const vin = findKeyInObject(item, header.key) || ''
        let vehicleUUID = ''
        if (item.Vehicle && (item.Vehicle.UUID || item.Vehicle.Uuid || item.Vehicle.uuid)) {
          vehicleUUID = item.Vehicle.UUID || item.Vehicle.Uuid || item.Vehicle.uuid
        } else {
          vehicleUUID = findKeyInObject(item, 'Vehicle.UUID') || ''
        }
        const registration =
          findKeyInObject(item, 'Vehicle.Registration') ||
          findKeyInObject(item, 'registration') ||
          findKeyInObject(item, 'ArchiveRow.Registration') ||
          ''
        const vehicleIdentifier = vehicleUUID || registration
        const workorderUUID = findKeyInObject(item, 'Workorder.UUID') || undefined
        const updatedAt = findKeyInObject(item, 'updatedAt') || undefined
        const isVinColumn = header.key?.toLowerCase().includes('vin') || header.name?.toLowerCase().includes('vin')
        const resourceId =
          findKeyInObject(item, 'ArchiveRow.InvoiceUUID') ||
          findKeyInObject(item, 'Invoice.WorkorderInvoiceUUID') ||
          findKeyInObject(item, 'Invoice.InventoryVehicleTransactionInvoiceUUID') ||
          findKeyInObject(item, 'Quotation.WorkorderQuotationUUID') ||
          undefined
        return {
          key: header.key,
          cellComponent:
            isVinColumn && hasVinMaskingFeature && useVinUnmask
              ? () =>
                  vin ? (
                    <MaskedComponent
                      vin={vin}
                      vehicleUUID={vehicleIdentifier}
                      workorderUUID={workorderUUID}
                      updatedAt={updatedAt}
                      useVinUnmask={useVinUnmask}
                      registration={registration}
                      resourceID={resourceId}
                    />
                  ) : null
              : getCellComponent(header),
          content: isVinColumn && hasVinMaskingFeature ? vin : header.action || findKeyInObject(item, header.key)
        }
      })
    }))

    if (onRowMap) {
      knockData = onRowMap(knockData, data, headers)
    }

    return <WrappedComponent {...props} headers={knockHeaders} data={knockData} paging={data.Paging} structure={data.Structure} />
  }

  return ListDataMapper
}

export default withListData
